// @flow
import { createSelector } from 'reselect';
import { createShallowEqualSelector } from '../selector_factories';
import type { GlobalState } from '../../constants';

type QueryFragment = Function;
type Identifier = string | number | Array<string> | Array<number>;

const passState = (state, _id) => state;
const passId = (_state, id) => id;

// combine the finder and the modifiers into a single function
const composeQuery = (finder: Function, modifiers: Array<Function>) => {
  const composedQuery = modifiers.reduce((accQuery, modifierFactory) => {
    const modifier = modifierFactory();
    return (state, entity) => modifier(state, accQuery(state, entity));
  }, finder);

  return composedQuery;
};

// helper which allows us to cache selectors for individual entities by their ids.
// allows each to have its own memoized "thread"
const makeSelectorForEntityId = (makeSelector) => {
  const query_by_id = {};
  return (entity_id) => {
    if (!query_by_id[entity_id]){
      query_by_id[entity_id] = makeSelector();
    }
    return query_by_id[entity_id];
  };
};

// This function, query, is a factory which takes in a finder an an array of modifiers and returns a query-er.
// Generally only called while composing a query.
const query = (finder: QueryFragment, ...modifiers: Array<QueryFragment>) => {
  const selectorForEntityId = makeSelectorForEntityId(() => (
    // createSelector here is a minor performance improvement for unchanged globalState + ids
    createSelector([passState, passId], composeQuery(finder, modifiers))
  ));

  // this will run the resultant query. individual id are run through the query.
  // multiple ids are split into individual query "threads" and run independently of each other
  const doQuery = (state: GlobalState, id: Identifier) => {
    if (Array.isArray(id)){
      return id.map(entity_id => selectorForEntityId(entity_id)(state, entity_id));
    } else {
      return selectorForEntityId(id)(state, id);
    }
  };

  // finally, we add a layer of memoization, so that if the query's results are unchanged we return the previous result
  return createShallowEqualSelector([doQuery], results => results);
};

export default query;
